/**
 * Input shape validation.
 *
 * Structure-only checks (required fields, types, formats). Semantic rules
 * (group/select combinations, pagination ambiguity, engine limits) live in
 * `plan/validate-plan.ts`. Extend the shape checks here as the input grows.
 */
import { QueryBuilderError } from '../errors/query-builder-error';
import type { FieldPath, FilterExpression, QueryInput } from './input-types';

const FIELD_TYPES = new Set(['string', 'number', 'boolean', 'date']);

/** Validate an optional nested field path: an array of non-empty strings. */
function validateFieldPath(
    path: FieldPath | undefined,
    jsonPath: string,
    errors: QueryBuilderError[],
): void {
    if (path === undefined) {
        return;
    }
    if (!Array.isArray(path) || path.length === 0) {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_INPUT',
                path: jsonPath,
                message: 'path must be a non-empty array of strings.',
            }),
        );
        return;
    }
    path.forEach((segment, i) => {
        if (typeof segment !== 'string' || segment.length === 0) {
            errors.push(
                new QueryBuilderError({
                    code: 'INVALID_INPUT',
                    path: `${jsonPath}[${i}]`,
                    message: 'path segments must be non-empty strings.',
                }),
            );
        }
    });
}

/** Walk a filter tree validating each leaf condition's nested path. */
export function validateFilterPaths(
    expr: FilterExpression,
    jsonPath: string,
    errors: QueryBuilderError[],
): void {
    if ('and' in expr) {
        expr.and.forEach((e, i) =>
            validateFilterPaths(e, `${jsonPath}.and[${i}]`, errors),
        );
        return;
    }
    if ('or' in expr) {
        expr.or.forEach((e, i) =>
            validateFilterPaths(e, `${jsonPath}.or[${i}]`, errors),
        );
        return;
    }
    if ('not' in expr) {
        validateFilterPaths(expr.not, `${jsonPath}.not`, errors);
        return;
    }
    validateFieldPath(expr.path, `${jsonPath}.path`, errors);
}

export function validateInputShape(input: QueryInput): QueryBuilderError[] {
    const errors: QueryBuilderError[] = [];

    if (!input || typeof input !== 'object') {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_INPUT',
                path: '$',
                message: 'Query input must be an object.',
            }),
        );
        return errors;
    }

    // Source
    if (!input.source || typeof input.source.name !== 'string') {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_INPUT',
                path: '$.source.name',
                message: 'source.name is required and must be a string.',
            }),
        );
    } else if (input.source.name.includes(';')) {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_IDENTIFIER',
                path: '$.source.name',
                message: 'Identifier cannot contain semicolon.',
            }),
        );
    }

    if (input.source && input.source.schema !== undefined) {
        if (typeof input.source.schema !== 'string') {
            errors.push(
                new QueryBuilderError({
                    code: 'INVALID_INPUT',
                    path: '$.source.schema',
                    message: 'source.schema must be a string when provided.',
                }),
            );
        } else if (input.source.schema.includes(';')) {
            errors.push(
                new QueryBuilderError({
                    code: 'INVALID_IDENTIFIER',
                    path: '$.source.schema',
                    message: 'Identifier cannot contain semicolon.',
                }),
            );
        }
    }

    // Select field types
    input.select?.forEach((sel, i) => {
        if (!sel.field || typeof sel.field !== 'string') {
            errors.push(
                new QueryBuilderError({
                    code: 'INVALID_INPUT',
                    path: `$.select[${i}].field`,
                    message: 'select field is required.',
                }),
            );
        }
        if (!FIELD_TYPES.has(sel.type)) {
            errors.push(
                new QueryBuilderError({
                    code: 'INVALID_FIELD_TYPE',
                    path: `$.select[${i}].type`,
                    message: `Invalid field type: "${sel.type}".`,
                }),
            );
        }
        validateFieldPath(sel.path, `$.select[${i}].path`, errors);
    });

    // Nested paths on group-by, aggregations, order-by and filter leaves.
    input.groupBy?.forEach((g, i) =>
        validateFieldPath(g.path, `$.groupBy[${i}].path`, errors),
    );
    input.aggregations?.forEach((a, i) =>
        validateFieldPath(a.path, `$.aggregations[${i}].path`, errors),
    );
    input.orderBy?.forEach((o, i) => {
        if ('field' in o) {
            validateFieldPath(o.path, `$.orderBy[${i}].path`, errors);
        }
    });
    if (input.filter) {
        validateFilterPaths(input.filter, '$.filter', errors);
    }

    if (input.limit !== undefined && typeof input.limit !== 'number') {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_INPUT',
                path: '$.limit',
                message: 'limit must be a number.',
            }),
        );
    }
    if (input.skip !== undefined && typeof input.skip !== 'number') {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_INPUT',
                path: '$.skip',
                message: 'skip must be a number.',
            }),
        );
    }
    if (input.cursor !== undefined && !Array.isArray(input.cursor)) {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_INPUT',
                path: '$.cursor',
                message: 'cursor must be an array.',
            }),
        );
    }

    // TODO: extend shape checks for filter/aggregation/groupBy/having/orderBy
    // structure as the contract is exercised by real inputs.

    return errors;
}
